// Dashboard com métricas, gráficos e notas recentes
import { useState, useEffect, useRef } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Loading } from '../../components/common/Loading';
import { 
  FileText, 
  TrendingUp, 
  DollarSign, 
  AlertCircle,
  Plus,
  ArrowRight,
  FileCheck,
  FileSearch,
  FileMinus,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getNotasFiscais } from '../../services/notasFiscaisService';
import { getClientes } from '../../services/clientesService';
import { getProdutos } from '../../services/produtosService';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { vincularUsuarioEmpresa } from '../../services/usuariosService';
import { supabase } from '../../lib/supabase';
import { OnboardingWizard } from '../../components/onboarding/OnboardingWizard';
import type { NotaFiscal, Empresa } from '../../types';

export function Dashboard() {
  const { empresaSelecionada, empresas, loading: empresaLoading, setEmpresaSelecionada, adicionarEmpresa, isFirstTime, setIsFirstTime } = useEmpresa();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(false); // Começar como false, será true apenas quando carregar
  const [isCreatingEmpresa, setIsCreatingEmpresa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const carregandoRef = useRef(false); // Evitar múltiplas chamadas simultâneas
  const [stats, setStats] = useState({
    totalNotas: 0,
    notasEmitidas: 0,
    notasRascunho: 0,
    notasCanceladas: 0,
    valorTotal: 0,
    totalClientes: 0,
    totalProdutos: 0,
    notasPorTipo: {
      NFe: 0,
      NFSe: 0,
      NFCe: 0,
    },
  });
  const [notasRecentes, setNotasRecentes] = useState<NotaFiscal[]>([]);
  const [vendasMensais, setVendasMensais] = useState<{ mes: string; valor: number }[]>([]);

  // Exibir onboarding se for primeiro uso
  useEffect(() => {
    if (isFirstTime && !empresaLoading && empresas.length === 0) {
      setShowOnboarding(true);
    }
  }, [isFirstTime, empresaLoading, empresas.length]);

  useEffect(() => {
    // Evitar múltiplas chamadas simultâneas
    if (carregandoRef.current) return;
    
    if (empresaSelecionada) {
      carregarDados();
    } else {
      // Se não há empresa selecionada, não precisa carregar dados
      setLoading(false);
      carregandoRef.current = false;
    }
  }, [empresaSelecionada]);

  // Timeout de segurança separado: se após 5 segundos ainda estiver loading, forçar false
  // Isso garante que mesmo com erro de recursão RLS, o loading será parado
  useEffect(() => {
    if (loading && empresaSelecionada) {
      const timeout = setTimeout(() => {
        console.warn('[Dashboard] Timeout de segurança: parando loading após 5 segundos');
        setLoading(false);
        carregandoRef.current = false;
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [loading, empresaSelecionada]);

  const carregarDados = async () => {
    if (!empresaSelecionada || carregandoRef.current) return;
    
    try {
      carregandoRef.current = true;
      setLoading(true);
      setError(null);

      // Carregar dados em paralelo com tratamento de erro individual
      // Se algum serviço falhar, retornar array vazio em vez de quebrar
      const [notas, clientes, produtos] = await Promise.allSettled([
        getNotasFiscais(empresaSelecionada.id).catch(() => []),
        getClientes(empresaSelecionada.id).catch(() => []),
        getProdutos(empresaSelecionada.id).catch(() => []),
      ]);
      
      // Extrair valores dos resultados
      const notasData = notas.status === 'fulfilled' ? notas.value : [];
      const clientesData = clientes.status === 'fulfilled' ? clientes.value : [];
      const produtosData = produtos.status === 'fulfilled' ? produtos.value : [];

      // Calcular estatísticas
      const totalNotas = notasData.length;
      const notasEmitidas = notasData.filter(n => n.status === 'emitida').length;
      const notasRascunho = notasData.filter(n => n.status === 'rascunho').length;
      const notasCanceladas = notasData.filter(n => n.status === 'cancelada').length;
      const valorTotal = notasData
        .filter(n => n.status === 'emitida')
        .reduce((sum, n) => sum + n.total, 0);

      const notasPorTipo = {
        NFe: notasData.filter(n => n.tipo === 'NFe').length,
        NFSe: notasData.filter(n => n.tipo === 'NFSe').length,
        NFCe: notasData.filter(n => n.tipo === 'NFCe').length,
      };

      // Agrupar vendas por mês (últimos 6 meses)
      const vendasPorMes: { [key: string]: number } = {};
      const mesAtual = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const data = new Date(mesAtual);
        data.setMonth(data.getMonth() - i);
        const mesKey = data.toLocaleDateString('pt-BR', { month: 'short' });
        vendasPorMes[mesKey] = 0;
      }

      notasData
        .filter(n => n.status === 'emitida')
        .forEach(nota => {
          const dataEmissao = new Date(nota.dataEmissao);
          const mesKey = dataEmissao.toLocaleDateString('pt-BR', { month: 'short' });
          if (vendasPorMes[mesKey] !== undefined) {
            vendasPorMes[mesKey] += nota.total;
          }
        });

      const vendasMensaisData = Object.entries(vendasPorMes).map(([mes, valor]) => ({
        mes: mes.charAt(0).toUpperCase() + mes.slice(1),
        valor,
      }));

      setStats({
        totalNotas,
        notasEmitidas,
        notasRascunho,
        notasCanceladas,
        valorTotal,
        totalClientes: clientesData.length,
        totalProdutos: produtosData.length,
        notasPorTipo,
      });

      setNotasRecentes(notasData.slice(0, 5));
      setVendasMensais(vendasMensaisData);
    } catch (err: any) {
      console.error('[Dashboard] Erro ao carregar dados:', err);
      // Não mostrar erro para o usuário, apenas logar
      // Retornar dados vazios para não quebrar a interface
      setStats({
        totalNotas: 0,
        notasEmitidas: 0,
        notasRascunho: 0,
        notasCanceladas: 0,
        valorTotal: 0,
        totalClientes: 0,
        totalProdutos: 0,
        notasPorTipo: { NFe: 0, NFSe: 0, NFCe: 0 },
      });
      setNotasRecentes([]);
      setVendasMensais([]);
    } finally {
      setLoading(false);
      carregandoRef.current = false;
    }
  };
  
  // Dados para gráfico de notas por tipo
  const notasPorTipo = [
    { name: 'NFe', value: stats.notasPorTipo.NFe, color: '#0ea5e9' },
    { name: 'NFSe', value: stats.notasPorTipo.NFSe, color: '#10b981' },
    { name: 'NFCe', value: stats.notasPorTipo.NFCe, color: '#f59e0b' },
  ];

  // Se há empresas mas nenhuma selecionada, aguardar um pouco e forçar seleção
  useEffect(() => {
    if (empresas.length > 0 && !empresaSelecionada && !empresaLoading) {
      // Aguardar um pouco para dar tempo do contexto selecionar
      const timeout = setTimeout(() => {
        // Se ainda não selecionou, forçar seleção da primeira
        if (empresas.length > 0 && !empresaSelecionada) {
          console.log('[Dashboard] Forçando seleção da primeira empresa:', empresas[0].nomeFantasia);
          setEmpresaSelecionada(empresas[0]);
        }
      }, 1000); // Aguardar 1 segundo
      return () => clearTimeout(timeout);
    }
  }, [empresas.length, empresaSelecionada, empresaLoading, setEmpresaSelecionada]);

  // Se está carregando empresas E não há empresas, mostrar loading
  if (empresaLoading && empresas.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  // Se não há empresa selecionada, verificar se há empresas disponíveis
  // Se houver empresas, aguardar um pouco para que o contexto selecione automaticamente
  // Se não houver empresas, mostrar tela de criação
  if (!empresaSelecionada) {
    // Se está carregando empresas, SEMPRE aguardar (mesmo que não haja empresas ainda)
    if (empresaLoading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center py-12">
            <Loading />
          </div>
        </div>
      );
    }
    
    // Se há empresas mas nenhuma selecionada, aguardar um pouco mais (pode estar selecionando)
    if (empresas.length > 0 && !empresaSelecionada) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-center py-12">
            <Loading />
          </div>
        </div>
      );
    }
    
    // Se não há empresas cadastradas E não está carregando, mostrar tela de criação
    if (empresas.length === 0 && !empresaLoading) {
      return (
        <div className="space-y-6">
          <Card>
            <div className="text-center py-12 space-y-6">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Nenhuma empresa cadastrada
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Você ainda não possui empresas cadastradas. Crie uma empresa para começar a usar o sistema.
              </p>
              
              {/* Mensagem de erro */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={async () => {
                      // Criar empresa de teste automaticamente
                      try {
                        setIsCreatingEmpresa(true);
                        setError(null);
                        
                        console.log('[Dashboard] Iniciando criação de empresa de teste...');
                        
                        // Buscar usuário autenticado
                        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
                        
                        if (userError || !authUser) {
                          throw new Error('Usuário não autenticado');
                        }
                        
                        console.log('[Dashboard] Usuário autenticado:', authUser.id);
                        
                        // Criar empresa de teste diretamente no Supabase (evitar usar createEmpresa que pode causar recursão)
                        // Primeiro, criar o endereço
                        const { data: endereco, error: enderecoError } = await supabase
                          .from('enderecos')
                          .insert({
                            cep: '01310-100',
                            logradouro: 'Avenida Paulista',
                            numero: '1000',
                            complemento: 'Sala 100',
                            bairro: 'Bela Vista',
                            cidade: 'São Paulo',
                            estado: 'SP',
                          })
                          .select()
                          .single();
                        
                        if (enderecoError) {
                          console.error('[Dashboard] Erro ao criar endereço:', enderecoError);
                          throw new Error(`Erro ao criar endereço: ${enderecoError.message}`);
                        }
                        
                        console.log('[Dashboard] Endereço criado:', endereco.id);
                        
                        console.log('[Dashboard] Inserindo empresa usando função SQL...');
                        
                        // Dados da empresa para criar (usar para objeto Empresa também)
                        // Gerar CNPJ único baseado em timestamp para evitar duplicatas
                        const timestamp = Date.now();
                        const cnpjUnico = `12.345.678/0001-${timestamp.toString().slice(-2)}`;
                        
                        const empresaData = {
                          razao_social: 'Empresa Teste LTDA',
                          nome_fantasia: 'Empresa Teste',
                          cnpj: cnpjUnico,
                          inscricao_estadual: '123456789',
                          email: user?.email || 'teste@empresa.com',
                          telefone: '(11) 98765-4321',
                        };
                        
                        // Usar função SQL para criar empresa (contorna RLS recursão)
                        // Esta função usa SECURITY DEFINER para evitar recursão nas políticas RLS
                        // E cria conta automaticamente se não existir
                        const { data: empresaIdResult, error: empresaError } = await supabase.rpc(
                          'criar_empresa_teste',
                          {
                            p_razao_social: empresaData.razao_social,
                            p_nome_fantasia: empresaData.nome_fantasia,
                            p_cnpj: empresaData.cnpj,
                            p_inscricao_estadual: empresaData.inscricao_estadual,
                            p_email: empresaData.email,
                            p_telefone: empresaData.telefone,
                            p_endereco_id: endereco.id,
                            p_usuario_id: authUser.id, // ID do usuário para criar conta se necessário
                          }
                        );
                        
                        if (empresaError) {
                          console.error('[Dashboard] Erro ao criar empresa via RPC:', empresaError);
                          throw new Error(`Erro ao criar empresa: ${empresaError.message}`);
                        }
                        
                        if (!empresaIdResult) {
                          throw new Error('Empresa criada mas ID não retornado');
                        }
                        
                        const empresaId = empresaIdResult as string;
                        console.log('[Dashboard] Empresa criada com ID:', empresaId);
                        
                        // Vincular usuário à empresa PRIMEIRO (antes de criar objeto Empresa)
                        // Isso pode ser necessário para que as políticas RLS funcionem
                        console.log('[Dashboard] Vinculando usuário à empresa...');
                        try {
                          await vincularUsuarioEmpresa(authUser.id, empresaId, 'admin');
                          console.log('[Dashboard] Vínculo criado');
                        } catch (vinculoError: any) {
                          console.error('[Dashboard] Erro ao vincular:', vinculoError);
                          // Se falhar o vínculo, tentar continuar mesmo assim
                          // O usuário pode vincular manualmente depois
                        }
                        
                        // Criar objeto Empresa com dados que já temos (não buscar do banco para evitar RLS)
                        const novaEmpresa: Empresa = {
                          id: empresaId,
                          razaoSocial: empresaData.razao_social,
                          nomeFantasia: empresaData.nome_fantasia,
                          cnpj: empresaData.cnpj,
                          inscricaoEstadual: empresaData.inscricao_estadual,
                          email: empresaData.email,
                          telefone: empresaData.telefone,
                          endereco: {
                            cep: endereco.cep,
                            logradouro: endereco.logradouro,
                            numero: endereco.numero,
                            complemento: endereco.complemento || undefined,
                            bairro: endereco.bairro,
                            cidade: endereco.cidade,
                            estado: endereco.estado,
                          },
                        };
                        
                        // Adicionar empresa criada diretamente ao array do contexto
                        // Isso evita que o useEffect chame carregarEmpresas() novamente
                        adicionarEmpresa(novaEmpresa);
                        console.log('[Dashboard] Empresa adicionada ao contexto');
                        
                        // Selecionar a empresa criada IMEDIATAMENTE
                        setEmpresaSelecionada(novaEmpresa);
                        console.log('[Dashboard] Empresa selecionada IMEDIATAMENTE:', novaEmpresa.nomeFantasia);
                        
                        // NÃO chamar carregarEmpresas() aqui - causa loops
                        // A empresa já está no array e selecionada, o Dashboard vai carregar dados automaticamente
                        
                        setError(null);
                      } catch (err: any) {
                        console.error('[Dashboard] Erro ao criar empresa de teste:', err);
                        setError(err.message || 'Erro ao criar empresa de teste. Verifique as políticas RLS do Supabase.');
                      } finally {
                        setIsCreatingEmpresa(false);
                      }
                  }}
                  className="w-full sm:w-auto"
                  disabled={isCreatingEmpresa || empresaLoading}
                  isLoading={isCreatingEmpresa}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Empresa de Teste
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    // Recarregar empresas
                    window.location.reload();
                  }}
                  className="w-full sm:w-auto"
                >
                  Recarregar
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
    }
    
    // Se há empresas mas nenhuma selecionada, aguardar seleção automática
    // Isso não deveria acontecer pois o contexto seleciona automaticamente
    // Mas se acontecer, mostrar loading
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Notas Emitidas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {stats.notasEmitidas}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.totalNotas} total
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <FileCheck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </Card>
        
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Valor Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(stats.valorTotal)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Emitidas
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
        
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Clientes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {stats.totalClientes}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Cadastrados
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <FileSearch className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>
        
        <Card hover>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Produtos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {stats.totalProdutos}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Cadastrados
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Vendas Mensais */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Vendas Mensais
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={vendasMensais}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="valor" 
                stroke="#0ea5e9" 
                strokeWidth={2}
                name="Valor (R$)"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        
        {/* Gráfico de Notas por Tipo */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Notas por Tipo
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={notasPorTipo}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {notasPorTipo.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      
      {/* Status das Notas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Rascunhos
            </h3>
            <FileMinus className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {stats.notasRascunho}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Notas em rascunho
          </p>
        </Card>
        
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Emitidas
            </h3>
            <FileCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {stats.notasEmitidas}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Notas emitidas
          </p>
        </Card>
        
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Canceladas
            </h3>
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {stats.notasCanceladas}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Notas canceladas
          </p>
        </Card>
      </div>
      
      {/* Últimas Notas Fiscais */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Notas Fiscais Recentes
          </h2>
          <Link to="/app/consultas">
            <Button variant="secondary" size="sm">
              Ver Todas
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        
        {notasRecentes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400 mb-4">Nenhuma nota fiscal encontrada</p>
            <Link to="/app/emissao">
              <Button variant="primary">
                <Plus className="w-4 h-4 mr-2" />
                Emitir Primeira Nota
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {notasRecentes.map((nota) => (
              <div
                key={nota.id}
                onClick={() => navigate(`/app/notas/${nota.id}`)}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {nota.tipo} - {nota.numero}/{nota.serie}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {nota.destinatario.nome} • {formatDate(nota.dataEmissao)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(nota.total)}
                  </p>
                  <Badge
                    variant={
                      nota.status === 'emitida' ? 'success' :
                      nota.status === 'cancelada' ? 'danger' :
                      nota.status === 'rascunho' ? 'warning' : 'default'
                    }
                  >
                    {nota.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      
      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboarding}
        onComplete={() => {
          setShowOnboarding(false);
          setIsFirstTime(false);
        }}
      />
    </div>
  );
}
